import { IConfig } from '@cian/config/shared';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { applyMiddleware, combineReducers, compose, createStore as _createStore, Store } from 'redux';
import thunkMiddleware, { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { activeScreenReducer, TActiveScreen, TActiveScreenActions } from './activeScreen';
import { categoriesReducer, IGroup, TCategoriesAction } from './categories';
import { failedActionReducer, TFailedAction, TFailedActionState } from './failedAction';
import { ISubscriptionsState, subscriptionsReducer, TSubscriptionsActions } from './subscriptions';
import { IFastFiltersUserSchema } from '../repositories/monolith-python/entities/schemas/FastFiltersUserSchema';

export type TThunkAction<R = void> = ThunkAction<R, IAppState, IStoreContext, TActions>;
export type TThunkDispatch = ThunkDispatch<IAppState, IStoreContext, TActions>;

export enum EDeviceType {
  desktop = 'desktop',
  tablet = 'tablet',
  phone = 'phone',
}

export enum ESiteType {
  desktop = 'desktop',
  tablet = 'tablet',
  mobile = 'mobile',
}

export interface IAppState {
  abGroup: number;
  activeScreen: TActiveScreen;
  subscriptions: ISubscriptionsState;
  failedAction: TFailedActionState;
  token: string | null;
  user: IFastFiltersUserSchema;
  categoriesGroups: IGroup[];
  showError: boolean;
  deviceType: TDeviceType;
}

export interface IStoreContext {
  config: IConfig;
  logger: ILogger;
  httpApi: IHttpApi;
}

export type TActions = TSubscriptionsActions | TFailedAction | TActiveScreenActions | TCategoriesAction;

export type TDeviceType = EDeviceType | null;
export type TSiteType = ESiteType | null;

/**
 * Типизированный redux store
 */
export interface IReduxStore extends Store<IAppState, TActions> {
  dispatch: TThunkDispatch;
}

let storeReminder: IReduxStore;

export function createReduxStore(context: IStoreContext, preloadedState: IAppState, debugEnabled: boolean) {
  const rootReducer = combineReducers<IAppState, TActions>({
    abGroup: (state: number = preloadedState.abGroup) => state,
    activeScreen: activeScreenReducer,
    categoriesGroups: categoriesReducer,
    deviceType: (state: TDeviceType = preloadedState.deviceType) => state,
    failedAction: failedActionReducer,
    showError: (state: boolean = preloadedState.showError) => state,
    subscriptions: subscriptionsReducer,
    token: (state: string | null = preloadedState.token) => state,
    user: (state: IFastFiltersUserSchema = preloadedState.user) => state,
  });

  const composeEnhancers = debugEnabled ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose : compose;

  const storeEnchancers = composeEnhancers(applyMiddleware(thunkMiddleware.withExtraArgument(context)));

  const createStore = () => _createStore(rootReducer, preloadedState, storeEnchancers) as IReduxStore;

  if (process.env.NODE_ENV === 'development' && debugEnabled) {
    if (!storeReminder) {
      storeReminder = createStore();
    }

    storeReminder.replaceReducer(rootReducer);

    return storeReminder;
  }

  return createStore();
}
export { ICategory } from './categories';
export { IGroup } from './categories';
